// Quick Guides — the index page. A gallery of the one-page cheat sheets, shown as
// little paper cards you can open or 📌 pin to your workspace. New guides are
// added to GUIDES below and appear here automatically.
import { navigate } from "../router/router.js";
import { isAuthed } from "../lib/auth.js";
import { setPinned, fetchWorkspace } from "../lib/data.js";

export const GUIDES = [
  { nav: "wordclasses", emoji: "🧱", title: "Word Classes", desc: "The building blocks — what each kind of word does.", level: "A1" },
  { nav: "soundmap", emoji: "🔊", title: "Sound Map", desc: "How every letter and digraph sounds.", level: "A1" },
  { nav: "core100", emoji: "🗺️", title: "Core 100 Words", desc: "The highest-frequency words, grouped.", level: "A1" },
  { nav: "wordorder", emoji: "🧩", title: "Word Order", desc: "How a sentence is built, plus the big verbs.", level: "A1" },
  { nav: "stressaccents", emoji: "´", title: "Stress & Accents", desc: "The two stress rules and when to write the accent.", level: "A1" },
  { nav: "regularverbs", emoji: "🔁", title: "Regular Verbs", desc: "-ar / -er / -ir conjugations.", level: "A1", tense: "Present" },
  { nav: "irregularverbs", emoji: "⚡", title: "Irregular Verbs", desc: "The families, each with a full model verb.", level: "A1", tense: "Present" },
  { nav: "bigfour", emoji: "⭐", title: "The Big 4 Verbs", desc: "ser · estar · tener · hacer.", level: "A1", tense: "Present" },
  { nav: "advancedwordorder", emoji: "🪡", title: "Advanced Word Order", desc: "Object pronouns, adjectives, and where they sit.", level: "A2" },
];

const LEVEL_COLORS = {
  A1: "bg-sage-100 text-sage-700",
  A2: "bg-amber-100 text-amber-700",
  B1: "bg-rose-100 text-rose-700",
  B2: "bg-violet-100 text-violet-700",
  C1: "bg-sky-100 text-sky-700",
  C2: "bg-slate-100 text-slate-700",
};

export async function quickGuidesView(app) {
  const authed = isAuthed();
  let pinned = new Set();
  if (authed) {
    try {
      const ws = await fetchWorkspace();
      pinned = new Set(ws.filter((w) => w.collection_type === "guide" && w.is_pinned).map((w) => w.item_id));
    } catch {
      /* pins are a bonus — page still works without them */
    }
  }

  const cardHTML = (g) => {
    const isP = pinned.has(g.nav);
    return `
      <div class="paper sketch-box-sm p-4 relative">
        <button data-go="${g.nav}" class="text-left flex items-start gap-3 w-full pr-7">
          <span class="text-3xl leading-none">${g.emoji}</span>
          <span>
            <span class="block font-script text-2xl text-sage-800 leading-none">${g.title}</span>
            <span class="block text-xs text-sage-500 mt-1" style="font-family:Inter,sans-serif">${g.desc}</span>
            <span class="flex items-center gap-1.5 mt-2" style="font-family:Inter,sans-serif">
              <span class="text-[10px] font-bold px-1.5 py-0.5 rounded ${LEVEL_COLORS[g.level] || LEVEL_COLORS.A1}">${g.level || "A1"}</span>
              ${g.tense ? `<span class="text-[10px] px-1.5 py-0.5 rounded bg-white border border-sage-200 text-sage-500">${g.tense} tense</span>` : ""}
            </span>
          </span>
        </button>
        ${authed ? `<button data-pin-guide="${g.nav}" aria-label="Pin to workspace" title="${isP ? "Pinned" : "Pin to workspace"}" class="absolute top-2 right-2 text-lg leading-none hover:opacity-100" style="opacity:${isP ? "1" : "0.35"}">📌</button>` : ""}
      </div>`;
  };

  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <h2 class="text-4xl font-script text-sage-800 mb-1">Quick Guides 📒</h2>
      <p class="text-sage-600 mb-5" style="font-family:Inter,sans-serif">One-page cheat sheets to glance at whenever you need them. Tap one to open${authed ? ", or 📌 to pin it to your workspace" : ""}.</p>
      <div class="grid gap-3 sm:grid-cols-2">${GUIDES.map(cardHTML).join("")}</div>
    </section>`;

  app.querySelectorAll("[data-go]").forEach((b) => b.addEventListener("click", () => navigate(b.dataset.go)));
  app.querySelectorAll("[data-pin-guide]").forEach((b) =>
    b.addEventListener("click", async (e) => {
      e.stopPropagation();
      const nav = b.dataset.pinGuide;
      const isP = pinned.has(nav);
      b.disabled = true;
      try {
        await setPinned("guide", nav, !isP);
        if (isP) pinned.delete(nav);
        else pinned.add(nav);
        b.style.opacity = isP ? "0.35" : "1";
        b.title = isP ? "Pin to workspace" : "Pinned";
      } catch (_) {
        /* ignore */
      } finally {
        b.disabled = false;
      }
    })
  );
  window.scrollTo({ top: 0, behavior: "smooth" });
}
